import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Form } from "react-bootstrap";
import { fetchCurrentStore, fetchStores } from "../../api/api";
import { useUser } from "../../UserContext";

const normalizeList = (data) => (
    Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : [])
);

export const useAccountingScope = () => {
    const { user } = useUser();
    const isAdmin = Boolean(user?.is_superuser || user?.can_switch_stores);
    const [stores, setStores] = useState([]);
    const [currentStore, setCurrentStore] = useState(null);
    const [storeId, setStoreId] = useState(null);
    const [loadingScope, setLoadingScope] = useState(true);
    const [scopeError, setScopeError] = useState(null);

    useEffect(() => {
        if (!user) return;

        let cancelled = false;
        const loadScope = async () => {
            setLoadingScope(true);
            setScopeError(null);
            try {
                const [activeStore, storeData] = await Promise.all([
                    fetchCurrentStore(),
                    isAdmin ? fetchStores() : Promise.resolve([]),
                ]);
                if (cancelled) return;

                setCurrentStore(activeStore || null);
                setStores(normalizeList(storeData));
                setStoreId(user?.is_superuser ? "all" : String(activeStore.id));
            } catch (error) {
                if (cancelled) return;
                console.error("Error loading accounting scope", error);
                setScopeError("Unable to determine the accounting store scope.");
                setStoreId(null);
            } finally {
                if (!cancelled) setLoadingScope(false);
            }
        };

        loadScope();
        return () => {
            cancelled = true;
        };
    }, [user, isAdmin]);

    const options = useMemo(() => [
        ...(user?.is_superuser ? [{ value: "all", label: "Overall Company" }] : []),
        ...stores.map((store) => ({ value: String(store.id), label: store.name })),
    ], [stores, user?.is_superuser]);

    const selectedOption = options.find(
        (option) => String(option.value) === String(storeId)
    ) || null;

    const scopeLabel = storeId === "all"
        ? "Overall Company"
        : selectedOption?.label || currentStore?.name || "Current Store";

    return {
        isAdmin,
        stores,
        storeId,
        setStoreId,
        options,
        selectedOption,
        scopeLabel,
        loadingScope,
        scopeError,
        currentStore,
    };
};

export const AccountingScopeSelect = ({ scope, label = "Report Scope" }) => {
    if (!scope.isAdmin) return null;

    return (
        <Form.Group>
            <Form.Label>{label}</Form.Label>
            <Select
                options={scope.options}
                value={scope.selectedOption}
                onChange={(option) => scope.setStoreId(option?.value || "all")}
                isSearchable
                isClearable={false}
            />
        </Form.Group>
    );
};
